import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import authService from '@/services/authService';
import { getAdminBasePath } from '@/utils/adminPaths';
import customerService, {
  CustomerListItem,
  CustomerListCounts,
  CustomerListPagination,
} from '@/services/customerService';

export const CustomerListPage: React.FC = () => {
  const navigate = useNavigate();
  const arenaSlug = authService.getSlug() || localStorage.getItem('arenaSlug') || '';
  const userRole = authService.getRole();
  const isCashier = userRole === 'staff';

  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [counts, setCounts] = useState<CustomerListCounts>({ all: 0, active: 0, new: 0 });
  const [pagination, setPagination] = useState<CustomerListPagination>({
    page: 1,
    limit: 10,
    total_items: 0,
    total_pages: 1,
  });
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'new'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCustomers = useCallback(async () => {
    if (!arenaSlug) return;
    try {
      setLoading(true);
      const res = await customerService.getCustomers(arenaSlug, {
        search: debouncedSearch.trim(),
        tab: activeTab,
        page: pagination.page,
        limit: 10,
      });
      setCustomers(res.customers || []);
      setCounts(res.counts || { all: 0, active: 0, new: 0 });
      setPagination(
        res.pagination || {
          page: 1,
          limit: 10,
          total_items: 0,
          total_pages: 1,
        }
      );
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal memuat daftar pelanggan'));
    } finally {
      setLoading(false);
    }
  }, [arenaSlug, debouncedSearch, activeTab, pagination.page]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleExportCsv = async () => {
    if (!arenaSlug || isCashier) return;
    try {
      setExporting(true);
      await customerService.exportCustomersCsv(arenaSlug, {
        search: debouncedSearch.trim(),
        tab: activeTab,
      });
      showToast.success('Data pelanggan berhasil di-export ke CSV');
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal mengunduh CSV'));
    } finally {
      setExporting(false);
    }
  };

  const adminBase = getAdminBasePath();

  const getInitials = (name: string) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const formatRupiah = (val: number) => {
    return 'Rp. ' + Math.round(val).toLocaleString('id-ID');
  };

  const paginationInfo = useMemo(() => {
    if (pagination.total_items === 0) return 'Menampilkan 0 - 0 dari 0 pelanggan';
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total_items);
    return `Menampilkan ${start} - ${end} dari ${pagination.total_items} pelanggan`;
  }, [pagination]);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-12">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Pelanggan</h1>
        </div>

        {!isCashier && (
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting || loading}
            className="rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white px-5 py-2.5 text-sm font-semibold shadow-xs inline-flex items-center justify-center gap-2 transition-colors shrink-0 cursor-pointer disabled:opacity-50 self-start sm:self-auto"
          >
            <Icon
              icon={exporting ? 'ph:spinner-bold' : 'ph:download-simple-bold'}
              className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`}
            />
            <span>{exporting ? 'Mengunduh...' : 'Export CSV'}</span>
          </button>
        )}
      </div>

      {/* Main Card Wrapper */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white shadow-xs overflow-hidden">
        {/* Card Header Toolbar: Search & Status Filters */}
        <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Search Input */}
          <div className="relative w-full sm:w-80">
            <Icon
              icon="ph:magnifying-glass"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nama atau nomor HP"
              className="w-full rounded-full border border-[#E0E6ED] bg-white pl-10 pr-8 py-2 text-xs sm:text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <Icon icon="ph:x-circle-fill" className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveTab('all');
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'all'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
              }`}
            >
              Semua{' '}
              <span className={activeTab === 'all' ? 'text-[#FC480D]' : 'text-[#8C8C8C]'}>
                {counts.all}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('active');
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'active'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
              }`}
            >
              Aktif{' '}
              <span className={activeTab === 'active' ? 'text-[#FC480D]' : 'text-[#8C8C8C]'}>
                {counts.active}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('new');
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'new'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50'
              }`}
            >
              Baru{' '}
              <span className={activeTab === 'new' ? 'text-[#FC480D]' : 'text-[#8C8C8C]'}>
                {counts.new}
              </span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-xs sm:text-sm">
            <thead>
              <tr className="bg-[#F8F9FA] border-y border-[#F0F2F5] text-xs font-semibold text-[#0E1726]">
                <th className="py-3 px-6 text-left font-semibold">Pelanggan</th>
                <th className="py-3 px-4 text-right font-semibold">Booking</th>
                <th className="py-3 px-4 text-right font-semibold">Jam main</th>
                {!isCashier && <th className="py-3 px-4 text-right font-semibold">Total belanja</th>}
                <th className="py-3 px-6 text-left font-semibold">Kunjungan terakhir</th>
                <th className="py-3 px-4 text-left font-semibold">Lapangan favorit</th>
                <th className="py-3 px-6 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F2F5]">
              {loading ? (
                // Skeleton Rows
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-200" />
                        <div className="space-y-1.5">
                          <div className="w-28 h-3.5 bg-gray-200 rounded" />
                          <div className="w-20 h-2.5 bg-gray-200 rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="w-6 h-3.5 bg-gray-200 rounded ml-auto" />
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="w-12 h-3.5 bg-gray-200 rounded ml-auto" />
                    </td>
                    {!isCashier && (
                      <td className="py-4 px-4 text-right">
                        <div className="w-20 h-3.5 bg-gray-200 rounded ml-auto" />
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="w-24 h-3.5 bg-gray-200 rounded" />
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-20 h-3.5 bg-gray-200 rounded" />
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="w-4 h-4 bg-gray-200 rounded ml-auto" />
                    </td>
                  </tr>
                ))
              ) : customers.length === 0 ? (
                // Empty state
                <tr>
                  <td colSpan={isCashier ? 6 : 7} className="py-16 text-center px-4">
                    <div className="max-w-xs mx-auto space-y-3">
                      <div className="w-14 h-14 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center mx-auto text-[#FC480D]">
                        <Icon icon="ph:users-duotone" className="w-7 h-7" />
                      </div>
                      <h3 className="text-sm font-semibold text-[#0E1726]">
                        {debouncedSearch ? 'Pelanggan tidak ditemukan' : 'Belum ada pelanggan'}
                      </h3>
                      <p className="text-xs text-[#8C8C8C]">
                        {debouncedSearch
                          ? `Tidak ada pelanggan dengan kata kunci "${debouncedSearch}". Coba cari dengan nama atau nomor HP lain.`
                          : 'Data pelanggan akan otomatis tercatat saat pertama kali melakukan booking atau transaksi.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                // Real rows
                customers.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`${adminBase}/customers/${c.id}`)}
                    className="hover:bg-gray-50/70 transition-colors cursor-pointer group"
                  >
                    {/* Pelanggan Name & Phone */}
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#FFF5EE] text-[#FC480D] font-semibold text-xs flex items-center justify-center shrink-0 uppercase">
                          {getInitials(c.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[#0E1726] truncate">{c.name}</span>
                            {c.is_new && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600">
                                Baru
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#8C8C8C] mt-0.5">{c.formatted_phone}</p>
                        </div>
                      </div>
                    </td>

                    {/* Booking count */}
                    <td className="py-4 px-4 text-right font-semibold text-[#0E1726] whitespace-nowrap">
                      {c.total_booking}
                    </td>

                    {/* Jam Main */}
                    <td className="py-4 px-4 text-right text-gray-600 whitespace-nowrap">
                      {c.total_jam_main} jam
                    </td>

                    {/* Total Belanja */}
                    {!isCashier && (
                      <td className="py-4 px-4 text-right font-semibold text-[#0E1726] whitespace-nowrap">
                        {formatRupiah(c.total_belanja)}
                      </td>
                    )}

                    {/* Kunjungan Terakhir */}
                    <td className="py-4 px-6 whitespace-nowrap">
                      <div>
                        <span className="block text-[#0E1726]">
                          {c.last_booking_date_str || '-'}
                        </span>
                        {c.last_booking_relative && (
                          <span className="text-xs text-[#8C8C8C] block mt-0.5">
                            {c.last_booking_relative}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Lapangan Favorit */}
                    <td className="py-4 px-4 text-gray-600 whitespace-nowrap">
                      {c.favorite_field || '-'}
                    </td>

                    {/* Caret */}
                    <td className="py-4 px-6 text-right">
                      <Icon
                        icon="ph:caret-right"
                        className="w-4 h-4 text-gray-400 group-hover:text-[#FC480D] transition-colors inline-block"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && customers.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-[#F0F2F5] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs text-[#8C8C8C]">
            <div>{paginationInfo}</div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center gap-1.5 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
                  }
                  disabled={pagination.page <= 1}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Sebelumnya
                </button>

                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => {
                  if (
                    p === 1 ||
                    p === pagination.total_pages ||
                    Math.abs(p - pagination.page) <= 1
                  ) {
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPagination((prev) => ({ ...prev, page: p }))}
                        className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center transition-colors cursor-pointer ${
                          pagination.page === p
                            ? 'bg-[#FC480D] text-white shadow-xs'
                            : 'border border-[#E0E6ED] text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  }
                  if (p === 2 && pagination.page > 3) {
                    return (
                      <span key={p} className="px-1 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  if (
                    p === pagination.total_pages - 1 &&
                    pagination.page < pagination.total_pages - 2
                  ) {
                    return (
                      <span key={p} className="px-1 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  type="button"
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.min(pagination.total_pages, prev.page + 1),
                    }))
                  }
                  disabled={pagination.page >= pagination.total_pages}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Berikutnya
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerListPage;
